import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { MatPaginator, MatSort } from '@angular/material';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { debounceTime } from 'rxjs/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/operators/distinctUntilChanged';
import { first } from 'rxjs/operators/first';
import { fromEvent } from 'rxjs/observable/fromEvent';
import { map } from 'rxjs/operators/map';
import { merge } from 'rxjs/observable/merge';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';

import { isBefore, isDate, isValid } from 'date-fns';

import { DataTableSource } from '../../classes/DataTableSource/datatablesource';

import { ApplicationService, SchoolInfo } from '../../services/application/application.service';
import { ErrorService } from '../../services/error/error.service';
import { UserService, UserInfo } from '../../services/user/user.service';

@Component({
    selector: 'app-student-search',
    styleUrls: ['studentsearch.component.css'],
    templateUrl: 'studentsearch.component.html',
})
export class StudentSearchComponent implements AfterViewInit, OnInit, OnDestroy {
    @ViewChild('firstinput') firstinput: ElementRef;
    @ViewChild('lastinput') lastinput: ElementRef;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild('studentidinput') studentidinput: ElementRef;
    @ViewChild(MatSort) sorter: MatSort;

    private _subRelease: Subject<void> = new Subject<void>();

    // Class
    AllHeaders: string[] = ['StudentID', 'LastName', 'FirstName', 'MiddleName', 'School', 'Grade', 'Actions'];
    AvailableSchools: SchoolInfo[];
    AvailableSchoolGrades: string[] = ['All', '06', '07', '08', '09', '10', '11', '12'];
    HasAnyUpcoming: boolean;
    PartialViewReady: boolean;
    ReferrerSource: string;
    SearchConducted: boolean;
    SearchData: DataTableSource<SearchResult> | null;
    SearchForm: FormGroup;
    SearchResults: SearchResult[];
    SelectedSchoolYear: string;
    ShowSearch: boolean;
    SmallDisplay: boolean;
    User: UserInfo;
    VisibleColumns: string[];
    VisibleHeaders: string[];

    constructor(
        private appService: ApplicationService,
        private errorService: ErrorService,
        private fb: FormBuilder,
        private mediaChange: ObservableMedia,
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService
    ) {}

    ngAfterViewInit() {
        const self = this;

        // Observe for key presses on the search fields
        const firstev = fromEvent<KeyboardEvent>(this.firstinput.nativeElement, 'keyup').pipe(
            takeUntil(self._subRelease),
            debounceTime(150),
            distinctUntilChanged()
        );
        const lastev = fromEvent<KeyboardEvent>(this.lastinput.nativeElement, 'keyup').pipe(
            takeUntil(self._subRelease),
            debounceTime(150),
            distinctUntilChanged()
        );
        const stuidev = fromEvent<KeyboardEvent>(this.studentidinput.nativeElement, 'keyup').pipe(
            takeUntil(self._subRelease),
            debounceTime(150),
            distinctUntilChanged()
        );

        merge(firstev, lastev, stuidev)
            .pipe(takeUntil(self._subRelease))
            .subscribe(keystroke => {
                if (keystroke.keyCode === 13) {
                    // User hit an ENTER. Perform search.
                    // self.HandleSearch();
                }
            });
    }

    ngOnDestroy() {
        this._subRelease.next();
        this._subRelease.complete();
    }

    ngOnInit() {
        const self = this;

        self.AvailableSchools = null;
        self.HasAnyUpcoming = false;
        self.PartialViewReady = false;
        self.ReferrerSource = null;
        self.SearchConducted = false;
        self.SearchResults = null;
        self.SelectedSchoolYear = null;
        self.ShowSearch = true;
        self.SearchData = new DataTableSource<SearchResult>(
            self.SearchResults,
            self.paginator,
            self.sorter,
            self.SortStudents.bind(self)
        );

        self.SmallDisplay = self.mediaChange.isActive('xs') || self.mediaChange.isActive('sm');

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subRelease))
            .subscribe(media => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns = self.VisibleHeaders.slice();

                self.ShowSearch = self.SmallDisplay ? !self.SearchConducted : true;
            });

        self.SearchForm = self.fb.group(
            {
                FirstName: [''],
                LastName: [''],
                StudentID: [''],
                Grade: [''],
                CollegeID: [null],
                CollegeStudentID: [''],
                Term: [''],
                SchoolNumber: [''],
            },
            {
                validator: function(ctrl: FormGroup) {
                    if (!ctrl) {
                        return null;
                    }

                    const form = ctrl.value;

                    if (
                        !form.FirstName &&
                        !form.LastName &&
                        !form.StudentID &&
                        !form.Grade &&
                        !form.CollegeID &&
                        !form.CollegeStudentID &&
                        !form.Term &&
                        !form.SchoolNumber
                    ) {
                        return { noneprovided: true };
                    }
                    if (
                        !form.StudentID &&
                        !form.Grade &&
                        !form.CollegeID &&
                        !form.CollegeStudentID &&
                        !form.Term &&
                        !form.SchoolNumber
                    ) {
                        // Check to make sure we have at least 5 characters combined from the firstname and lastname fields
                        const fn = (form.FirstName || '').length;
                        const ln = (form.LastName || '').length;

                        if (fn + ln < 5) {
                            return { TooFewCharacters: true };
                        }
                    }

                    return null;
                },
            }
        );

        // Wait for a first non-null User result before kicking off the page
        self.userService.UserSubject.pipe(
            takeUntil(self._subRelease),
            first(item => item && !!item.AuthToken)
        ).subscribe(_user => {
            self.User = _user;

            // Hook into items which can change (such as parameters or user authtoken updates)
            combineLatest(
                self.userService.UserSubject,
                self.route.params,
                self.appService.SelectedSchoolYearSubject,
                self.appService.GetAllSchools(self.User.AuthToken),
                function(user, params, selYear, schools) {
                    return {
                        User: user,
                        Params: params,
                        SelectedYear: selYear,
                        Schools: schools,
                    };
                }
            )
                .pipe(takeUntil(self._subRelease))
                .subscribe(
                    packet => {
                        self.User = packet.User;
                        self.ReferrerSource = packet.Params['Source'];
                        self.SelectedSchoolYear = packet.SelectedYear;
                        self.AvailableSchools = packet.Schools;

                        // Set the page as ready
                        self.PartialViewReady = true;
                    },
                    error => {
                        self.errorService.HandleServiceError(error);
                    }
                );
        });
    }

    // #region Clear Form Method

    ClearForm() {
        this.SearchForm.reset();

        this.firstinput.nativeElement.focus();
    }

    // #endregion

    // #region Handle Search Method

    HandleSearch() {
        const self = this;

        // If the current user is not a district admin or developer, then default the school number in the search to their work location.
        const searchParams = self.SearchForm.value;

        if (!self.User.IsDistrictAdmin && !self.User.IsDeveloper) {
            searchParams.SchoolNumber = self.User.SchoolNumber;
        }

        // Call to get the search results ('0937' - test school)
        self.appService
            .SearchForStudent(
                self.User.AuthToken,
                self.User.CurrentRole,
                self.SelectedSchoolYear,
                searchParams.SchoolNumber,
                searchParams.StudentID,
                searchParams.CollegeID,
                searchParams.Term,
                self.SelectedSchoolYear,
                searchParams.Grade,
                searchParams.FirstName,
                searchParams.LastName
            )
            .subscribe(
                results => {
                    self.SearchResults = results;
                    if (self.SmallDisplay) {
                        self.ShowSearch = false;
                    }
                    self.HasAnyUpcoming = false;

                    if (self.SearchResults && self.SearchResults.length) {
                        const upcoming =
                            self.SearchResults.find(item => {
                                return item.UpcomingSchoolYear === true;
                            }) != null;

                        self.HasAnyUpcoming = upcoming;

                        self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                        self.VisibleColumns = self.VisibleHeaders.slice();
                    } else {
                        self.VisibleColumns = ['NoData'];
                    }

                    self.SearchData.Update(self.SearchResults);
                    self.SearchConducted = true;
                },
                error => {
                    self.errorService.HandleServiceError(error);
                }
            );
    }

    // #endregion

    // #region Show Search Form Method

    ShowSearchForm() {
        this.ShowSearch = true;
        this.SearchConducted = false;
    }

    // #endregion

    // #region Sort Students Method

    SortStudents(First: SearchResult, Second: SearchResult) {
        const self = this;

        let PropA: string | number | Date;
        let PropB: string | number | Date;

        switch (self.sorter.active) {
            case 'StudentID':
                [PropA, PropB] = [First.StudentID, Second.StudentID];
                break;
            case 'LastName':
                [PropA, PropB] = [First.LastName, Second.LastName];
                break;
            case 'FirstName':
                [PropA, PropB] = [First.FirstName, Second.FirstName];
                break;
            case 'MiddleName':
                [PropA, PropB] = [First.MiddleName, Second.MiddleName];
                break;
            case 'School':
                [PropA, PropB] = [First.SchoolName, Second.SchoolName];
                break;
            case 'Grade':
                [PropA, PropB] = [First.GradeLevel, Second.GradeLevel];
                break;
        }

        if (isDate(PropA)) {
            if (!isDate(PropB)) {
                return 0;
            }
            if (!isValid(<Date>PropA) || !isValid(<Date>PropB)) {
                return 0;
            }

            if (isBefore(PropA, PropB)) {
                return -1 * (self.sorter.direction === 'asc' ? 1 : -1);
            }
            if (isBefore(PropB, PropA)) {
                return 1 * (self.sorter.direction === 'asc' ? 1 : -1);
            }

            return 0;
        } else {
            const ValueA = PropA != null && PropA !== '' ? (isNaN(+PropA) ? PropA : +PropA) : PropA;
            const ValueB = PropB != null && PropB !== '' ? (isNaN(+PropB) ? PropB : +PropB) : PropB;

            return (ValueA < ValueB ? -1 : 1) * (self.sorter.direction === 'asc' ? 1 : -1);
        }
    }

    // #endregion

    // #region View Student Method

    ViewStudent(student: SearchResult) {
        if (!student) {
            return;
        }

        // Route to the student view
        this.router.navigate(['/studentview', student.StudentID]);
    }

    // #endregion
}

// #region Custom Classes

// #region Search Result Class

export class SearchResult {
    FirstName?: string;
    LastName?: string;
    MiddleName?: string;
    StudentID?: string;
    GradeLevel?: string;
    CollegeID?: number;
    CollegeStudentID?: string;
    Term?: string;
    SchoolName?: string;
    SchoolNumber?: string;
    UpcomingSchoolYear?: boolean;
}

// #endregion

// #endregion



// WEBPACK FOOTER //
// ./src/app/studentsearch/studentsearch.component.ts